import React, { createContext, useCallback, useContext, useEffect, useMemo, useReducer } from 'react';
import type { TenantConfig, StageId } from '@alterrego/core-config';
import type { AuthContext, AuthProvider as AuthProviderType, AuthState, AlterregoUser } from './types';
import { AuthClient } from './client';
import { clearTokens, decodeJwt, getRefreshToken, getToken, isTokenExpired, storeTokens } from './token';

type AuthAction =
  | { type: 'LOADING' }
  | { type: 'AUTHENTICATED'; user: AlterregoUser; token: string }
  | { type: 'UNAUTHENTICATED' };

function authReducer(_state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'LOADING':
      return { status: 'loading', user: null, token: null };
    case 'AUTHENTICATED':
      return { status: 'authenticated', user: action.user, token: action.token };
    case 'UNAUTHENTICATED':
      return { status: 'unauthenticated', user: null, token: null };
  }
}

const Ctx = createContext<AuthContext | null>(null);

/**
 * Fournit l'authentification à l'arbre React.
 * Route vers le bon provider selon la config du stage.
 *
 * Usage :
 * ```tsx
 * <AuthProvider config={tenantConfig} stage="webapp">
 *   <App />
 * </AuthProvider>
 * ```
 */
export function AuthProvider({
  config,
  stage,
  children,
}: {
  config: TenantConfig;
  stage: StageId;
  children: React.ReactNode;
}) {
  const [state, dispatch] = useReducer(authReducer, {
    status: 'loading',
    user: null,
    token: null,
  });

  const stageConfig = config.stages[stage];
  const client = useMemo(() => new AuthClient(config.backend), [config.backend]);
  const env = stageConfig.env;

  // Au montage, tente de restaurer la session depuis le stockage local
  useEffect(() => {
    (async () => {
      // Auth désactivée sur ce stage (mockup)
      if (stageConfig.auth.includes('disabled')) {
        dispatch({ type: 'UNAUTHENTICATED' });
        return;
      }

      const token = await getToken();
      if (!token) {
        dispatch({ type: 'UNAUTHENTICATED' });
        return;
      }

      if (isTokenExpired(token)) {
        const refresh = await getRefreshToken();
        if (!refresh) {
          await clearTokens();
          dispatch({ type: 'UNAUTHENTICATED' });
          return;
        }
        try {
          const result = await client.refreshJwt(refresh, env);
          await storeTokens(result.jwt, result.refreshToken);
          const user = await client.getProfile(result.jwt, env);
          dispatch({ type: 'AUTHENTICATED', user, token: result.jwt });
        } catch {
          await clearTokens();
          dispatch({ type: 'UNAUTHENTICATED' });
        }
        return;
      }

      try {
        const user = await client.getProfile(token, env);
        dispatch({ type: 'AUTHENTICATED', user, token });
      } catch {
        await clearTokens();
        dispatch({ type: 'UNAUTHENTICATED' });
      }
    })();
  }, [client, env, stageConfig.auth]);

  const signIn = useCallback(
    async (provider: AuthProviderType) => {
      dispatch({ type: 'LOADING' });

      if (provider === 'apple') {
        // Dynamically import the Apple auth module
        const { signInWithApple } = await import('@alterrego/module-apple-auth');
        const appleResult = await signInWithApple({
          serviceId: config.authConfig.apple!.serviceId,
          redirectUri: config.authConfig.apple!.redirectUri,
        });

        const result = await client.exchangeAppleCode({
          code: appleResult.authorizationCode,
          idToken: appleResult.identityToken,
          bundleId: config.bundleId,
          env,
        });

        await storeTokens(result.jwt, result.refreshToken);
        dispatch({ type: 'AUTHENTICATED', user: result.user, token: result.jwt });
        return;
      }

      if (provider === 'mysafe') {
        // Future: import @alterrego/module-mysafe
        throw new Error('mySafe auth not yet implemented');
      }

      throw new Error(`Unknown auth provider: ${provider}`);
    },
    [client, config, env],
  );

  const signOut = useCallback(async () => {
    await clearTokens();
    dispatch({ type: 'UNAUTHENTICATED' });
  }, []);

  const refreshToken = useCallback(async () => {
    const refresh = await getRefreshToken();
    if (!refresh) return null;
    try {
      const result = await client.refreshJwt(refresh, env);
      await storeTokens(result.jwt, result.refreshToken);
      dispatch({
        type: 'AUTHENTICATED',
        user: state.user!,
        token: result.jwt,
      });
      return result.jwt;
    } catch {
      await clearTokens();
      dispatch({ type: 'UNAUTHENTICATED' });
      return null;
    }
  }, [client, env, state.user]);

  const value = useMemo<AuthContext>(
    () => ({ ...state, signIn, signOut, refreshToken }),
    [state, signIn, signOut, refreshToken],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/**
 * Hook pour accéder à l'état et aux actions d'authentification.
 *
 * ```tsx
 * const { status, user, signIn, signOut } = useAuth();
 * ```
 */
export function useAuth(): AuthContext {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useAuth must be used within an <AuthProvider>');
  return ctx;
}
